import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  ErrorCode,
  type ApiErrorBody,
  type ConfirmOfferBody,
  type FinancierChecklistState,
  type FinancierDocumentRequestBody,
  type OfferFieldKey,
  type OfferStatus,
  type OfferUploadState,
  type RecordRejectionBody,
  type RecordSignatureBody,
  type RequestNoteBody,
  type SignatureMethod,
} from '@brimo/shared';
import { ApiError, GENERIC_ERROR_MESSAGE, UNAUTHENTICATED_EVENT, api, apiUpload } from '../../../lib/api';
import { queryRoots, useRealtime, type RealtimeEvent } from '../../../lib/events';

/**
 * Financier view of a financing (Finanzierer 03–11, `docs/dev/api-financier.md`): the checklist, the offers, the
 * signature and their writes. Queries live under `['financing', id, …]` so that invalidating the record refreshes them.
 */

// --- the record fields this folder reads (`GET /financings/:id`, docs/dev/api-financings.md) -----------------------

export interface RecordAction {
  allowed: boolean;
  reason: string | null;
}

/** The part of F1's `FinancingRecord` the financier components read; F1's record type satisfies it structurally. */
export interface FinancierRecordFields {
  id: string;
  numberLabel: string;
  viewer: 'team' | 'financier';
  title: string;
  stage: { key: string; label: string };
  status: 'active' | 'lost';
  actions: {
    requestDocuments: RecordAction;
    markRequested: RecordAction;
    uploadOffer: RecordAction;
    recordSignature: RecordAction;
    notFinanceable: RecordAction;
  };
  linked: { contact: { id: string; name: string } };
  request: {
    requestedOn: string;
    offersExpectedOn: string | null;
    where: string | null;
    note: string | null;
    byShort: string | null;
  } | null;
  openTasks: ReadonlyArray<{ id: string; title: string; contextLine: string; dueLabel: string }>;
  customerProfile: {
    employment: string | null;
    householdNetCents: number | null;
    equityCents: number | null;
    volumeCents: number | null;
    volumeTag: string | null;
    selfDisclosureHref: string;
  } | null;
  advisor: {
    id: string;
    name: string;
    initials: string;
    line: string;
    email: string;
    phone: string | null;
    hint: string | null;
  } | null;
}

// --- response shapes --------------------------------------------------------------------------------------------------

export interface ChecklistItem {
  key: string;
  title: string;
  state: FinancierChecklistState;
  sub: string | null;
  documentId: string | null;
  requestId: string | null;
}

export interface ChecklistGroup {
  done: number;
  total: number;
  items: ChecklistItem[];
}

export interface ChecklistView {
  customer: ChecklistGroup;
  financier: ChecklistGroup;
  object: { done: number; total: number };
  warning: { title: string; text: string | null } | null;
  complete: boolean;
  completeCard: { title: string; text: string } | null;
  brokerNote: { text: string; count: number; href: string } | null;
  requestable: Array<{ documentTypeId: string; title: string; sub: string | null; preselected: boolean }>;
}

export interface ReviewField {
  key: OfferFieldKey;
  label: string;
  value: string | null;
  input: string | number | null;
  tag: 'erkannt' | 'unsicher' | null;
  hint: string | null;
}

export interface OfferReview {
  offerId: string;
  fileId: string;
  fileLine: string;
  fields: ReviewField[];
}

export interface OfferUploadRow {
  offerId: string;
  fileId: string;
  fileName: string;
  sizeLabel: string | null;
  state: OfferUploadState;
  message: string;
  prefill: { bank: string | null; loanCents: number | null };
}

export interface OfferRow {
  id: string;
  bank: string;
  sourceLine: string | null;
  status: OfferStatus;
  statusLabel: string;
  tone: 'info' | 'accent' | 'danger' | 'neutral';
  line: string;
  validLine: string | null;
  dimmed: boolean;
  actions: { choose: boolean; archive: boolean; replace: boolean };
}

export interface OffersView {
  review: OfferReview | null;
  uploads: OfferUploadRow[];
  rows: OfferRow[];
  expiringBanner: { title: string; text: string } | null;
  allRejected: { title: string; text: string } | null;
  unitSuggestion: { text: string; unitId: string; advisorName: string | null } | null;
  request: { text: string } | null;
  stageHint: string | null;
  canEdit: boolean;
}

export interface SignatureView {
  kpis: Array<{ label: string; value: string; sub: string | null }>;
  defaults: { bank: string | null; method: SignatureMethod };
  signed: {
    on: string;
    bank: string;
    methodLabel: string;
    contract: { documentId: string | null; name: string } | null;
  } | null;
}

export interface DocumentType {
  id: string;
  key: string;
  name: string;
  category: string;
}

// --- queries ------------------------------------------------------------------------------------------------------------

export const financierKeys = {
  checklist: (id: string) => [...queryRoots.financing, id, 'checklist'] as const,
  offers: (id: string) => [...queryRoots.financing, id, 'offers'] as const,
  signature: (id: string) => [...queryRoots.financing, id, 'signature'] as const,
  documentTypes: () => ['document-types'] as const,
};

export function useChecklist(id: string, enabled = true) {
  return useQuery({
    queryKey: financierKeys.checklist(id),
    queryFn: ({ signal }) => api<ChecklistView>(`/financings/${id}/checklist`, { signal }),
    enabled,
  });
}

export function useOffers(id: string, enabled = true) {
  return useQuery({
    queryKey: financierKeys.offers(id),
    queryFn: ({ signal }) => api<OffersView>(`/financings/${id}/offers`, { signal }),
    enabled,
  });
}

export function useSignature(id: string, enabled = true) {
  return useQuery({
    queryKey: financierKeys.signature(id),
    queryFn: ({ signal }) => api<SignatureView>(`/financings/${id}/signature`, { signal }),
    enabled,
  });
}

export function useDocumentTypes(enabled = true) {
  return useQuery({
    queryKey: financierKeys.documentTypes(),
    queryFn: ({ signal }) => api<{ items: DocumentType[] }>('/document-types', { signal }),
    enabled,
    staleTime: 10 * 60_000,
  });
}

/** Refetches the financier queries of this financing on `financing.updated` and `document.updated` (api-financier „Realtime"). */
export function useFinancierRealtime(financingId: string): void {
  const queryClient = useQueryClient();
  const refresh = useCallback(
    (event: RealtimeEvent) => {
      const target = event.payload['financingId'];
      if (typeof target === 'string' && target !== financingId) return;
      void queryClient.invalidateQueries({ queryKey: [...queryRoots.financing, financingId] });
    },
    [queryClient, financingId],
  );
  useRealtime('financing.updated', refresh);
  useRealtime('document.updated', refresh);
}

/** Refreshes the record and everything under it (checklist, offers, signature). */
export function useRefreshFinancing(financingId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: [...queryRoots.financing, financingId] }),
    [queryClient, financingId],
  );
}

// --- writes ---------------------------------------------------------------------------------------------------------------

export function requestDocuments(
  id: string,
  body: FinancierDocumentRequestBody,
): Promise<{ requestIds: string[]; messageId: string | null; taskId: string | null }> {
  return api(`/financings/${id}/document-requests`, { method: 'POST', body });
}

export function saveRequestNote(
  id: string,
  body: RequestNoteBody,
): Promise<{ financingId: string; stageMoved: boolean; taskId: string | null }> {
  return api(`/financings/${id}/request-note`, { method: 'PUT', body });
}

export interface OfferActionResult {
  offerId: string;
  status: OfferStatus;
  stageMoved: boolean;
}

export function confirmOffer(
  id: string,
  offerId: string,
  body: ConfirmOfferBody,
): Promise<OfferActionResult> {
  return api(`/financings/${id}/offers/${offerId}/confirm`, { method: 'POST', body });
}

export function discardOffer(id: string, offerId: string): Promise<void> {
  return api(`/financings/${id}/offers/${offerId}`, { method: 'DELETE' });
}

export function archiveOffer(id: string, offerId: string): Promise<OfferActionResult> {
  return api(`/financings/${id}/offers/${offerId}/archive`, { method: 'POST', body: {} });
}

export function chooseOffer(id: string, offerId: string): Promise<OfferActionResult> {
  return api(`/financings/${id}/offers/${offerId}/choose`, {
    method: 'POST',
    body: { customerConsented: true },
  });
}

export function recordRejection(id: string, body: RecordRejectionBody): Promise<OfferActionResult> {
  return api(`/financings/${id}/offers/rejection`, { method: 'POST', body });
}

export function suggestUnit(id: string, unitId: string): Promise<{ notified: boolean }> {
  return api(`/financings/${id}/suggest-unit`, { method: 'POST', body: { unitId } });
}

export function reportNotFinanceable(id: string, note: string | null): Promise<unknown> {
  return api(`/financings/${id}/not-financeable`, {
    method: 'POST',
    body: note ? { note } : {},
  });
}

export function recordSignature(
  id: string,
  body: RecordSignatureBody,
): Promise<{ financingId: string; contractTaskId: string | null }> {
  return api(`/financings/${id}/signature`, { method: 'POST', body });
}

export function uploadContract(id: string, file: File): Promise<{ documentId: string; fileId: string }> {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiUpload(`/financings/${id}/contract`, form);
}

/** `POST /documents` for a checklist item (FZ-03 „Datei hochladen", a customer item on the customer's behalf). */
export function uploadChecklistDocument(input: {
  financingId: string;
  party: 'customer' | 'financier';
  documentTypeId: string;
  documentRequestId: string | null;
  file: File;
}): Promise<unknown> {
  const form = new FormData();
  form.append('financingId', input.financingId);
  form.append('party', input.party);
  form.append('documentTypeId', input.documentTypeId);
  if (input.documentRequestId) form.append('documentRequestId', input.documentRequestId);
  form.append('file', input.file, input.file.name);
  return apiUpload('/documents', form);
}

export function requestFinancingAccess(id: string): Promise<unknown> {
  return api('/approvals/access', {
    method: 'POST',
    body: { kind: 'records', entity: 'financing', ids: [id], query: '' },
  });
}

// --- offer upload with progress (FZ-10 „Die Verbindung wurde unterbrochen bei 62 %.") -------------------------------

export interface OfferUploadResult {
  offerId: string;
  state: 'pending' | 'unavailable';
  documentId: string;
  fileId: string;
}

/** The upload broke off (network error or abort) at `percent`; a server answer arrives as `ApiError` instead. */
export class UploadInterruptedError extends Error {
  readonly percent: number;

  constructor(percent: number) {
    super('upload interrupted');
    this.name = 'UploadInterruptedError';
    this.percent = percent;
  }
}

function apiErrorOf(status: number, text: string): ApiError {
  let body: ApiErrorBody['error'] = { code: ErrorCode.INTERNAL, message: GENERIC_ERROR_MESSAGE };
  try {
    const parsed = JSON.parse(text) as Partial<ApiErrorBody> | null;
    if (parsed?.error) body = parsed.error;
  } catch {
    // a proxy answer without the JSON envelope keeps the generic message
  }
  if (status === 401 && body.code === ErrorCode.UNAUTHENTICATED) {
    window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
  }
  return new ApiError(status, body);
}

/**
 * `POST /financings/:id/offers` through `XMLHttpRequest` so that the upload progress is visible (`apiUpload` has none).
 * Rejects with `ApiError` for a server answer and with `UploadInterruptedError` when the connection breaks.
 */
export function uploadOfferWithProgress(
  financingId: string,
  file: File,
  options: { replacesOfferId?: string | null; onProgress: (percent: number) => void },
): Promise<OfferUploadResult> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    if (options.replacesOfferId) form.append('replacesOfferId', options.replacesOfferId);
    form.append('file', file, file.name);
    const request = new XMLHttpRequest();
    let percent = 0;
    request.open('POST', `/api/financings/${financingId}/offers`);
    request.withCredentials = true;
    request.setRequestHeader('Accept', 'application/json');
    request.upload.onprogress = (event) => {
      if (!event.lengthComputable || event.total === 0) return;
      percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
      options.onProgress(percent);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        try {
          resolve(JSON.parse(request.responseText) as OfferUploadResult);
        } catch {
          reject(apiErrorOf(500, ''));
        }
        return;
      }
      reject(apiErrorOf(request.status, request.responseText));
    };
    request.onerror = () => reject(new UploadInterruptedError(percent));
    request.onabort = () => reject(new UploadInterruptedError(percent));
    request.send(form);
  });
}
